import { useRef, useEffect } from 'react'

import { useEventContext } from 'context/EventContext'

import detachTask from './detachTask'

// IntersectionObserver singleton instance
let observer = null

const defaultConfig = {
  threshold: 1.0,
  rootMargin: '0px 0px 0px 0px',
}

const searchInMap = (map, condition) => {
  for (const entry of map.values()) {
    if (condition(entry)) return true
  }
  return false
}

const observeRef = (map, ref, _observer) =>
  map.set(ref.current, { sentEvents: false }) && _observer.observe(ref.current)

const eventsAreSentForRef = (map, ref, _observer) =>
  map.set(ref.current, { sentEvents: true }) && _observer.unobserve(ref)

const observerHandler = (map, sendEvent) => (entries, _observer) => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return
    const { ievtId } = entry.target.dataset || {}
    // @ts-ignore
    detachTask(sendEvent, window.__GDM_EVENTS__[ievtId])
    eventsAreSentForRef(map, entry.target, _observer)
  })
}

/*
  useImpressionsObserver usage example:
  const Component = () => {
    const observerOptions = {
      rootMargin: '0px 0px 0px 0px',
      threshold: 1.0
    }
    const { ref, addEvent } = useImpressionsObserver(observerOptions)
    return (
      <div
        ref={ref}
        {...addEvent({
          engagementComponent: 'event category',
          engagementDetails: 'event label',
          engagementDestination: 'event action',
          name: 'eventGA4 name',
        })}
      ></div>
    )
  }
 */
const useImpressionsObserver = (observerOptions = defaultConfig) => {
  const ref = useRef(null)
  const { sendEvent, addEvent: getContextEventProps } = useEventContext()
  const observedRefsMap = useRef(new Map())
  const addEvent = evt => getContextEventProps({ ...evt, isImpressionEvent: true })

  useEffect(() => {
    if (!ref.current) return

    if (!(observer instanceof IntersectionObserver))
      observer = new IntersectionObserver(observerHandler(observedRefsMap.current, sendEvent), observerOptions)

    if (!observedRefsMap.current.has(ref.current)) observeRef(observedRefsMap.current, ref, observer)

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const hasPendingEntries = searchInMap(observedRefsMap.current, mapEntry => mapEntry.sentEvents === false)
      hasPendingEntries || observer.disconnect()
    }
  }, [observerOptions.threshold, observerOptions.rootMargin, ref, sendEvent, observerOptions])
  return { ref, addEvent }
}

export default useImpressionsObserver
